// The device tile used by the Rooms, Devices and Favourites screens — a direct
// port of the Card + Stack the Flutter app repeats in roomsScreen.dart,
// devicesScreen.dart and favDevicesScreen.dart:
//
//   Card (ButtonColor, radius 20, elevation 6)
//     image (DeviceID artwork)
//     auto-scrolling device name
//     [optional] 📍 Location > Room line          (favourites only)
//   ⭐ favourite badge, overhanging the top-right corner  (-6, -6)
//   liveness badge, top-left

import React from 'react';
import {
  Box, Card, CardActionArea, IconButton, useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AutoHorizontalScroll from './AutoHorizontalScroll';
import LivenessBadge from './LivenessBadge';
import { deviceImage } from '../../../core/devices/deviceAssets';

export default function DeviceCard({
  device,
  onOpen,
  onToggleFavourite,
  imageSize = 90,
  showLocation = false,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const uno = device.UniqueNumber;
  const isFav = Number(device.favourite) === 1;
  const name = device.DeviceName
    || device.DeviceTypeName
    || `Unnamed ${device.ActualDeviceName || 'Device'}`;

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Card
        elevation={6}
        sx={{ bgcolor: c.button, borderRadius: '20px', height: '100%' }}
      >
        <CardActionArea
          onClick={() => onOpen && onOpen(device)}
          sx={{
            height: '100%',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Box
              component="img"
              src={deviceImage(device.DeviceID)}
              alt=""
              sx={{ width: imageSize, maxWidth: '100%', objectFit: 'contain' }}
            />
            <Box sx={{ height: 16 }} />
            <AutoHorizontalScroll text={name} />
            {showLocation && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', mt: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 16, color: c.text }} />
                <Box sx={{ width: 4 }} />
                <AutoHorizontalScroll
                  text={`${device.LocationName || 'No Location'} > ${device.RoomName || 'Common Device'}`}
                />
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>

      {/* Liveness badge — top-left, over the card. */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
        <LivenessBadge uniqueNumber={uno} />
      </Box>

      {/* ⭐ favourite badge — overhangs the corner exactly like Flutter's
          Positioned(top: -6, right: -6). Amber when set, background colour when
          not, so it reads as "outline" against the white circle. */}
      {onToggleFavourite && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleFavourite(device, isFav); }}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            p: 0.75,
            bgcolor: '#fff',
            boxShadow: '0 0 4px rgba(0,0,0,0.26)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <StarIcon sx={{ fontSize: 20, color: isFav ? '#FFC107' : c.back }} />
        </IconButton>
      )}
    </Box>
  );
}
